// The one place that knows the API's URLs. Every endpoint the admin depends on is listed here.
import type { OpRecord } from "./canvas/collab.ts";
import type { CanvasOp } from "./canvas/graph.ts";
import type {
  Bindings,
  CanvasInfo,
  CanvasScope,
  CanvasSession,
  CanvasSnapshot,
  CanvasTemplate,
  RefSummary,
  RunResult,
} from "./canvas/types.ts";
import { ASSUME_ALL, detectFeatures, type Features } from "./features.ts";
import type { HandleItem } from "./mentions.ts";
import { normalizeHandles } from "./mentions.ts";
import type {
  AcceptResult,
  AssetUrl,
  CameraPreset,
  Costs,
  DialogueLine,
  Element,
  ElementReference,
  ElementSubtype,
  ElementVersion,
  Episode,
  Estimate,
  EstimateInput,
  ForkResult,
  GenerateSceneInput,
  GenerateShotInput,
  Generation,
  ImportedItem,
  Layer,
  ModelInfo,
  Page,
  PlacedShot,
  Project,
  ProjectBoard,
  ReferenceRole,
  Scene,
  SceneEstimateInput,
  Selection,
  Shot,
  ShotRow,
  StaleItem,
  Take,
  TakesResponse,
  Tier,
  UploadTicket,
  Workspace,
} from "./types.ts";

export type Query = Record<string, string | number | boolean | undefined | null>;

/** `base` + `path` + query string. Empty/undefined query values are dropped. */
export function buildUrl(base: string, path: string, query?: Query): string {
  let end = base.length;
  while (end > 0 && base[end - 1] === "/") end--; // linear, unlike /\/+$/
  const b = base.slice(0, end);
  const p = path.startsWith("/") ? path : `/${path}`;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return `${b}${p}${s ? `?${s}` : ""}`;
}

/** Bearer for both a Supabase access token and an API key (fsk_…). */
export function authHeaders(token: string | null, hasBody: boolean): Record<string, string> {
  const h: Record<string, string> = { accept: "application/json" };
  if (token) h.authorization = `Bearer ${token}`;
  if (hasBody) h["content-type"] = "application/json";
  return h;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiOptions {
  baseUrl: string;
  getToken: () => Promise<string | null> | string | null;
  fetch?: typeof fetch;
  /** Called on a 401 so the app can drop the session. */
  onUnauthorized?: () => void;
}

interface Reading {
  answer: Promise<{ res: Response; text: string }>;
  /** Asked ahead and not taken yet. */
  kept: boolean;
  done: boolean;
}

/** How long a read asked ahead waits for the screen that wants it. */
const KEEP_AHEAD_MS = 5_000;

export function createApi(opts: ApiOptions) {
  const doFetch = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));

  /**
   * The same read asked twice at once is one request (each caller parses its own copy): by the same session, and with
   * no write begun or ended between the two. A read asked ahead (`preload`) is also kept for whoever asks next, once.
   * A POST that changes nothing (`asking`: a batch of URLs, a socket ticket) is no write.
   */
  const reading = new Map<string, Reading>();
  let writes = 0;
  let ahead = false;

  async function send(method: string, url: string, token: string | null, body: unknown) {
    const hasBody = body !== undefined;
    const res = await doFetch(url, {
      method,
      headers: authHeaders(token, hasBody),
      body: hasBody ? JSON.stringify(body) : undefined,
    });
    return { res, text: await res.text() };
  }

  async function read(url: string) {
    const after = writes;
    const keep = ahead;
    const token = await opts.getToken();
    const key = JSON.stringify([after, token, url]);
    const running = reading.get(key);
    if (running) {
      if (!keep && running.kept) {
        running.kept = false;
        if (running.done) reading.delete(key);
      }
      return running.answer;
    }
    const entry: Reading = {
      kept: keep,
      done: false,
      answer: send("GET", url, token, undefined),
    };
    reading.set(key, entry);
    const forget = () => {
      if (reading.get(key) === entry) reading.delete(key);
    };
    entry.answer.then(({ res }) => {
      entry.done = true;
      if (!entry.kept || !res.ok) forget();
      else setTimeout(forget, KEEP_AHEAD_MS);
    }, forget);
    return entry.answer;
  }

  /** Ask the reads `run` makes before anyone needs them. A failure stays silent: whoever needs it asks again. */
  function preload(run: () => readonly Promise<unknown>[]): void {
    ahead = true;
    try {
      for (const p of run()) p.catch(() => {});
    } finally {
      ahead = false;
    }
  }

  async function write(method: string, url: string, body: unknown) {
    writes++;
    try {
      return await send(method, url, await opts.getToken(), body);
    } finally {
      writes++;
    }
  }

  async function request<T>(
    method: string,
    path: string,
    o: { query?: Query; body?: unknown; asking?: boolean } = {},
  ): Promise<T> {
    const url = buildUrl(opts.baseUrl, path, o.query);
    const { res, text } = await (method === "GET"
      ? read(url)
      : o.asking
        ? send(method, url, await opts.getToken(), o.body)
        : write(method, url, o.body));
    let json: unknown;
    try {
      json = text ? JSON.parse(text) : undefined;
    } catch {
      json = undefined;
    }
    if (!res.ok) {
      if (res.status === 401) opts.onUnauthorized?.();
      const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | undefined)
        ?.error;
      throw new ApiError(
        res.status,
        err?.code ?? "http_error",
        err?.message ?? (text.slice(0, 200) || res.statusText || `HTTP ${res.status}`),
        err?.details,
      );
    }
    return json as T;
  }

  const get = <T>(path: string, query?: Query) => request<T>("GET", path, { query });
  const post = <T>(path: string, body: unknown = {}) => request<T>("POST", path, { body });

  /** Follow `next_cursor` until the list is complete (cap: 20 pages); answers every item and the last page fetched. */
  async function listAllPages<P extends Page<unknown>>(
    path: string,
    query: Query = {},
  ): Promise<{ items: P["items"]; last: P }> {
    const items: P["items"] = [];
    let cursor: string | undefined;
    let last: P;
    for (let i = 0; i < 20; i++) {
      last = await get<P>(path, { limit: 200, ...query, cursor });
      items.push(...last.items);
      if (!last.next_cursor) break;
      cursor = last.next_cursor;
    }
    return { items, last: last! };
  }

  const listAll = async <T>(path: string, query: Query = {}): Promise<T[]> =>
    (await listAllPages<Page<T>>(path, query)).items;

  const listTakes = async (path: string): Promise<TakesResponse> => {
    const { items, last } = await listAllPages<TakesResponse>(path);
    return { items, next_cursor: null, current_selection: last.current_selection };
  };

  const w = (ws: string) => `/workspaces/${ws}`;
  /** Ticket and PUT: the bytes are stored (or were already); a route lands them under its own rules. */
  const stage = async (ws: string, file: Blob) => {
    const bytes = await file.arrayBuffer();
    const sha256 = await sha256Hex(bytes);
    const mime = file.type || "application/octet-stream";
    const ticket = await post<UploadTicket>(`${w(ws)}/uploads`, { sha256, size: bytes.byteLength, mime });
    if (!ticket.exists) {
      if (!ticket.url) throw new ApiError(500, "upload_ticket", "the server sent no upload URL");
      const put = await doFetch(ticket.url, {
        method: ticket.method ?? "PUT",
        headers: ticket.headers,
        body: bytes,
      });
      if (!put.ok) throw new ApiError(put.status, "upload_failed", `upload failed (HTTP ${put.status})`);
    }
    return { sha256, size: bytes.byteLength, mime };
  };
  const c = (ws: string, id: string) => `${w(ws)}/canvases/${id}`;

  let features: Promise<Features> | undefined;
  const elementCache = new Map<string, { at: number; p: Promise<Element[]> }>();
  /** Drop the @ typeahead lists of a workspace (every production's), after an element changed its name or left. */
  const forgetElements = (ws: string) => {
    for (const key of elementCache.keys()) if (key.startsWith(`${ws}:`)) elementCache.delete(key);
  };

  return {
    baseUrl: opts.baseUrl,
    request,
    preload,
    // ── navigation ────────────────────────────────────────────────
    listWorkspaces: () => listAll<Workspace>("/workspaces"),
    listProjects: (ws: string) => listAll<Project>(`${w(ws)}/projects`),
    /** Your own order of the productions (ids first to last); productions not in it follow. */
    getProjectOrder: (ws: string) =>
      get<{ project_ids: string[] }>(`${w(ws)}/project-order`).then((r) => r.project_ids),
    setProjectOrder: (ws: string, ids: readonly string[]) =>
      request<{ project_ids: string[] }>("PUT", `${w(ws)}/project-order`, { body: { projectIds: ids } }),
    listEpisodes: (ws: string, projectId: string, opts: { includeArchived?: boolean } = {}) =>
      listAll<Episode>(`${w(ws)}/episodes`, {
        projectId,
        includeArchived: opts.includeArchived ? "true" : undefined,
      }),
    getEpisode: (ws: string, id: string) => get<Episode>(`${w(ws)}/episodes/${id}`),
    /** A null title falls back to "Episode N" in the UI. */
    renameEpisode: (ws: string, id: string, title: string | null) =>
      request<Episode>("PATCH", `${w(ws)}/episodes/${id}`, { body: { title } }),
    archiveEpisode: (ws: string, id: string) => post<Episode>(`${w(ws)}/episodes/${id}/archive`),
    /** Oldest first, every page: the newest version is the last of them, however many there are. */
    listScriptRevisions: async <T extends { rev_no: number }>(ws: string, episodeId: string) => {
      const items: T[] = [];
      const seen = new Set<string>();
      let cursor: string | undefined;
      // no page cap: the newest version comes last, so a cut list would answer an old script as the newest
      for (;;) {
        const page: Page<T> = await get<Page<T>>(`${w(ws)}/episodes/${episodeId}/script-revisions`, {
          limit: 200,
          cursor,
        });
        items.push(...page.items);
        if (!page.next_cursor) return items;
        if (seen.has(page.next_cursor)) throw new Error("script versions: the server repeated a page");
        seen.add(page.next_cursor);
        cursor = page.next_cursor;
      }
    },
    listScenes: (ws: string, episodeId: string) => listAll<Scene>(`${w(ws)}/scenes`, { episodeId }),
    listShotsOfScene: (ws: string, sceneId: string) => listAll<Shot>(`${w(ws)}/shots`, { sceneId }),
    /** Episodes, scenes, shots and every take of a production in one read. */
    getProjectBoard: (ws: string, projectId: string) =>
      get<ProjectBoard>(`${w(ws)}/projects/${projectId}/board`),
    listDialogueOfScene: (ws: string, sceneId: string) =>
      listAll<DialogueLine>(`${w(ws)}/dialogue-lines`, { sceneId }),
    listCharacters: (ws: string, projectId: string) =>
      listAll<Element>(`${w(ws)}/elements`, { projectId, subtype: "character" }),

    // ── building structure ────────────────────────────────────────
    createScene: (
      ws: string,
      body: { episodeId: string; code: string; position: number; slugline?: string },
    ) => post<Scene>(`${w(ws)}/scenes`, body),
    createShot: (ws: string, body: { projectId: string; code: string; description?: string }) =>
      post<Shot>(`${w(ws)}/shots`, body),
    /** Put an existing shot in a scene (a shot can be placed in several). */
    placeShot: (ws: string, shotId: string, body: { sceneId: string; position: number }) =>
      post<{ id: string; scene_id: string; shot_id: string; position: number }>(
        `${w(ws)}/shots/${shotId}/placements`,
        body,
      ),

    // ── takes and selection ───────────────────────────────────────
    listShotTakes: (ws: string, shotId: string) => listTakes(`${w(ws)}/shots/${shotId}/takes`),
    listSceneTakes: (ws: string, sceneId: string) => listTakes(`${w(ws)}/scenes/${sceneId}/takes`),
    selectTake: (
      ws: string,
      shotId: string,
      input: { kind: Layer; takeId: string; assetVersionId: string | null },
    ) => post<Selection>(`${w(ws)}/shots/${shotId}/selections`, input),
    selectSceneTake: (
      ws: string,
      sceneId: string,
      input: { kind: Layer; takeId: string; assetVersionId: string | null },
    ) => post<Selection>(`${w(ws)}/scenes/${sceneId}/selections`, input),
    listStale: (ws: string, episodeId: string) =>
      get<{ items: StaleItem[] }>(`${w(ws)}/episodes/${episodeId}/stale`).then((r) => r.items),

    // ── generation ────────────────────────────────────────────────
    generateForShot: (ws: string, shotId: string, input: GenerateShotInput) =>
      post<Generation>(`${w(ws)}/shots/${shotId}/generations`, input),
    generateForScene: (ws: string, sceneId: string, input: GenerateSceneInput) =>
      post<Generation>(`${w(ws)}/scenes/${sceneId}/generations`, input),
    promote: (ws: string, generationId: string) =>
      post<Generation>(`${w(ws)}/generations/${generationId}/promote`),
    retry: (ws: string, generationId: string) =>
      post<Generation>(`${w(ws)}/generations/${generationId}/retry`),
    cancel: (ws: string, generationId: string) =>
      post<Generation>(`${w(ws)}/generations/${generationId}/cancel`),
    reroll: (ws: string, takeId: string) => post<Generation>(`${w(ws)}/takes/${takeId}/reroll`),
    listGenerations: (ws: string, episodeId: string, state?: string) =>
      get<{ items: Generation[] }>(`${w(ws)}/generations`, { episodeId, state }).then((r) => r.items),
    listModels: (ws: string) => get<{ items: ModelInfo[] }>(`${w(ws)}/models`).then((r) => r.items),
    estimate: (ws: string, shotId: string, input: EstimateInput) =>
      post<Estimate>(`${w(ws)}/shots/${shotId}/estimate`, input),
    /** Price a scene-level generation (dialogue) without starting it. */
    estimateScene: (ws: string, sceneId: string, input: SceneEstimateInput) =>
      post<Estimate>(`${w(ws)}/scenes/${sceneId}/estimate`, input),

    // ── media and costs ───────────────────────────────────────────
    assetUrl: (ws: string, assetVersionId: string) =>
      get<AssetUrl>(`${w(ws)}/asset-versions/${assetVersionId}/url`),
    assetUrls: (ws: string, ids: string[]) =>
      request<{ items: (AssetUrl & { id: string })[] }>("POST", `${w(ws)}/asset-versions/urls`, {
        body: { ids },
        asking: true,
      }),
    episodeCosts: (ws: string, episodeId: string) => get<Costs>(`${w(ws)}/episodes/${episodeId}/costs`),

    // ── canvases ──────────────────────────────────────────────────
    listCanvases: (
      ws: string,
      q: { projectId?: string; scope?: CanvasScope; scopeId?: string; includeArchived?: boolean } = {},
    ) =>
      get<{ items: CanvasInfo[] }>(`${w(ws)}/canvases`, {
        projectId: q.projectId,
        scope: q.scope,
        scopeId: q.scopeId,
        includeArchived: q.includeArchived ? "true" : undefined,
      }).then((r) => r.items),
    createCanvas: (
      ws: string,
      body: {
        name: string;
        scope?: CanvasScope;
        scopeId?: string;
        projectId?: string;
        template?: { id: string; bindings: Bindings };
      },
    ) => post<CanvasSnapshot>(`${w(ws)}/canvases`, body),
    getCanvas: (ws: string, id: string) => get<CanvasSnapshot>(c(ws, id)),
    renameCanvas: (ws: string, id: string, name: string) =>
      request<CanvasInfo>("PATCH", c(ws, id), { body: { name } }),
    archiveCanvas: (ws: string, id: string) => post<CanvasInfo>(`${c(ws, id)}/archive`),
    canvasRefs: (ws: string, id: string) => get<Record<string, RefSummary>>(`${c(ws, id)}/refs`),
    canvasSession: (ws: string, id: string) => post<CanvasSession>(`${c(ws, id)}/session`),
    /** HTTP fallback for edits when no socket is available. Same op batch, same dedupe keys. */
    applyCanvasOps: (
      ws: string,
      id: string,
      body: { ops: CanvasOp[]; clientId?: string; clientOpId?: string },
    ) => post<{ record: OpRecord; duplicate: boolean }>(`${c(ws, id)}/ops`, body),
    listCanvasOps: (ws: string, id: string, after: number) =>
      get<{ records: OpRecord[]; resync: boolean }>(`${c(ws, id)}/ops`, { after, limit: 200 }),
    listCanvasTemplates: (ws: string, projectId?: string) =>
      get<{ items: CanvasTemplate[] }>(`${w(ws)}/canvas-templates`, { projectId }).then((r) => r.items),
    /** Starts the generation of a model node. 404 = the server has no run route yet. */
    runNode: (ws: string, canvasId: string, nodeId: string, runId?: string) =>
      post<RunResult>(`${c(ws, canvasId)}/nodes/${nodeId}/run`, runId ? { runId } : {}),

    // ── hub rows for the pickers ──────────────────────────────────
    listShots: (ws: string, q: { projectId?: string; episodeId?: string }) =>
      listAll<Shot>(`${w(ws)}/shots`, q),
    /** One shot by id, whether or not it sits in a scene. */
    getShot: (ws: string, shotId: string) => get<ShotRow>(`${w(ws)}/shots/${shotId}`),
    /** Generations of one shot (the takes made for it). */
    listShotGenerations: (ws: string, shotId: string) =>
      get<{ items: Generation[] }>(`${w(ws)}/generations`, { shotId, limit: 200 }).then((r) => r.items),
    /** Archive a shot (director). There is no restore route. */
    archiveShot: (ws: string, shotId: string) => post<Shot>(`${w(ws)}/shots/${shotId}/archive`),
    /** Put a shot that sits in no scene into an episode's default scene. Idempotent. */
    placeShotInEpisode: (ws: string, episodeId: string, shotId: string) =>
      post<PlacedShot>(`${w(ws)}/episodes/${episodeId}/place-shot`, { shotId }),

    // ── elements ──────────────────────────────────────────────────
    listElements: (
      ws: string,
      q: {
        projectId?: string;
        /** this production's own elements and the workspace-wide ones it uses */
        inProject?: string;
        subtype?: ElementSubtype;
      } = {},
    ) => listAll<Element>(`${w(ws)}/elements`, q),
    getElement: (ws: string, id: string) => get<Element>(`${w(ws)}/elements/${id}`),
    createElement: (
      ws: string,
      body: {
        projectId?: string;
        subtype: ElementSubtype;
        handle: string;
        name: string;
        data?: Record<string, unknown>;
        note?: string;
      },
    ) => post<Element>(`${w(ws)}/elements`, body),
    renameElement: async (ws: string, id: string, name: string) => {
      const el = await request<Element>("PATCH", `${w(ws)}/elements/${id}`, { body: { name } });
      forgetElements(ws);
      return el;
    },
    archiveElement: async (ws: string, id: string) => {
      const el = await post<Element>(`${w(ws)}/elements/${id}/archive`);
      forgetElements(ws);
      return el;
    },
    listElementVersions: (ws: string, id: string) =>
      listAll<ElementVersion>(`${w(ws)}/elements/${id}/versions`),
    createElementVersion: (ws: string, id: string, body: { data: Record<string, unknown>; note?: string }) =>
      post<ElementVersion>(`${w(ws)}/elements/${id}/versions`, body),
    /** Fork a version: next version with the same data and every reference copied. */
    forkElementVersion: (
      ws: string,
      id: string,
      body: { fromVersion?: number; data?: Record<string, unknown>; note?: string },
    ) => post<ForkResult>(`${w(ws)}/elements/${id}/fork`, body),
    listReferences: (ws: string, id: string, version: number) =>
      listAll<ElementReference>(`${w(ws)}/elements/${id}/versions/${version}/references`),
    createReference: (
      ws: string,
      id: string,
      version: number,
      body: { assetVersionId: string; role: string; canonical?: boolean; label?: string },
    ) => post<ElementReference>(`${w(ws)}/elements/${id}/versions/${version}/references`, body),

    /** Elements whose handle starts with `prefix`. Uses /handles when the server has it, else filters the list. */
    searchHandles: async (
      ws: string,
      q: { prefix?: string; projectId?: string; limit?: number },
      useRoute: boolean,
    ): Promise<HandleItem[]> => {
      if (useRoute) {
        try {
          return normalizeHandles(await get<unknown>(`${w(ws)}/handles`, q));
        } catch (e) {
          if (!(e instanceof ApiError && (e.status === 404 || e.status === 405))) throw e;
        }
      }
      const key = `${ws}:${q.projectId ?? ""}`;
      let hit = elementCache.get(key);
      if (!hit || Date.now() - hit.at > 30_000) {
        hit = { at: Date.now(), p: listAll<Element>(`${w(ws)}/elements`, { projectId: q.projectId }) };
        elementCache.set(key, hit);
        hit.p.catch(() => elementCache.delete(key));
      }
      const rows = await hit.p;
      // The whole list: the editor ranks prefix matches first and also matches names.
      return normalizeHandles(rows.filter((r) => !r.archived_at));
    },

    // ── uploads (ticket → PUT → complete) ─────────────────────────
    /** Ticket and PUT only: what a route that takes an upload (a blocking frame) needs to land it. */
    stageUpload: (ws: string, file: Blob) => stage(ws, file),
    uploadFile: async (
      ws: string,
      file: Blob & { name?: string },
      name?: string,
      /** put the file on a shot as a take of this layer, or file it under a production */
      target?: { shotId: string; layer: Layer } | { projectId: string },
    ): Promise<ImportedItem> => {
      const stored = await stage(ws, file);
      return post<ImportedItem>(`${w(ws)}/uploads/complete`, {
        ...stored,
        name: name ?? file.name ?? "upload",
        ...(target ? { target } : {}),
      });
    },

    // ── optional routes other workers add ─────────────────────────
    /** Which optional routes the server has (from its OpenAPI document; cached). */
    features: (): Promise<Features> => {
      features ??= request<unknown>("GET", "/openapi.json").then(detectFeatures, () => ASSUME_ALL);
      return features;
    },
    /** Generate a reference sheet view of an element (an element take). */
    generateSheet: (
      ws: string,
      elementId: string,
      body: {
        tier: Tier;
        sheet: { role: ReferenceRole; label?: string };
        version?: number;
        newTake?: { origin: "initial" | "variant"; label?: string };
      },
    ) =>
      post<Generation & { warnings?: string[]; take?: { id: string } }>(
        `${w(ws)}/elements/${elementId}/generations`,
        body,
      ),
    listElementTakes: (ws: string, elementId: string) =>
      listAll<Take>(`${w(ws)}/elements/${elementId}/takes`),
    /** Accept an image of an element take as a reference: forks a new version with it. */
    acceptTake: (
      ws: string,
      takeId: string,
      body: { assetVersionId: string; role: ReferenceRole; label?: string; canonical?: boolean },
    ) => post<AcceptResult>(`${w(ws)}/takes/${takeId}/accept`, body),
    listPresets: (ws: string) => get<{ items: CameraPreset[] }>(`${w(ws)}/presets`).then((r) => r.items),
  };
}

export type Api = ReturnType<typeof createApi>;

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(d, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Default API base: VITE_API_URL, else same origin `/v1`. */
export function defaultBaseUrl(env: { VITE_API_URL?: string } = import.meta.env): string {
  return env.VITE_API_URL?.trim() || "/v1";
}
